// Constants
import { ConfigNotFoundError } from "@ms/uno-errors/lib/local/errors/ConfigNotFoundError";
import { ClientAppId, ClientFlavor, ClientType, Environment, TenantRegion, allowedRolloutStagesForEnvironment } from "../AppConstants";
import { CopilotAgentLoadMaxRetries, CopilotAgentLoadTimeoutMs } from "../CopilotConstants";
import { MaxChoicesPerCustomField, MaxCustomFieldChoiceNameLength, MaxCustomFieldDefinitionNameLength, MaxNumberCustomFieldValue, MaxTextCustomFieldValueLength, MinChoicesPerCustomField, MinNumberCustomFieldValue } from "../CustomFieldConstants";
import { MAX_CUSTOM_VIEWS_COUNT } from "../CustomViewsConstants";
import { IrisSurfacePlacementIds } from "../IrisConstants";
import { OneDSEnvironment } from "../LoggingConstants";
import { RolloutStage } from "../RolloutStage";
import { SettingsConstants } from "../SettingsConstants";
import { TemplateIdConstants, TemplateListType } from "../TemplateConstants";
import { WebUrlSegments } from "../WebConstants";
import { AudienceGroup, TraceLevel } from "./ISettings";
// Utilities
import merge from "lodash/merge";
/**
 * Class to hold static data about the app. Data here should never mutate during the life of our app.
 */ export class AppContext {
    /**
     * Initialize the app based on given app context settings
     * @param settings The app context settings to be used
     * @param clientSessionIdOverride The client session ID to be used for the session
     */ static initializeFromSettings(settings, clientSessionIdOverride) {
        this._appConfiguration = settings.appConfiguration;
        // If a client session id override is available and the session id initial setup has not been done (empty server session id)
        if (!this._appConfiguration.changeGates.DisableClientDrivenSessionId && // Change gate to revert this behavior
        clientSessionIdOverride && !this._appConfiguration.sessionMetaData.serverSessionId) {
            // Use the dedicated field to store the server session id
            this._appConfiguration.sessionMetaData.serverSessionId = this._appConfiguration.sessionMetaData.sessionId;
            // Use the client session id for the standard field (this is what external systems like Kusto, monitors treat as a session)
            this._appConfiguration.sessionMetaData.sessionId = clientSessionIdOverride;
        }
    }
    /**
     * Get the change gates with the default values.
     * @deprecated Use getDefaultChangeGates instead
     */ static getDefaultOldChangeGates() {
        return {
            RemoveHomePageReadOnlyProp: true,
            ProjectTagTooltipFix: true,
            TurnOffTimelineZoomFlightMonth: true,
            RenderKeyDateWithinRowForRoadmap: true,
            DelayLoadingTopHeader: true,
            DatePickerVoiceAccess: true
        };
    }
    /**
     * Get the change gates with the default values.
     */ static getDefaultChangeGates() {
        return {
            DisableCachedBundleBootTelemetry: false,
            DisableHideUnlicensedFinishDateInTaskDetails: false,
            DisableFailClosedPremiumCreateWhenUpsellUnavailable: false,
            DisableStorePublishFrequencyMonitor: false,
            DisableUpsellSubscriptionStatusLoopFix: false,
            DisableTaskHydrationFromRouteCache: false,
            DisableTodoQueueAuthRefreshOn401: false,
            DisablePreserveRouteOnSameTabCachedLoad: false,
            DisableOpenNonPreviewableFilesDirectly: false,
            DisablePremiumPlanDateTimezoneFix: false,
            DisableTeamsTabConfigWriteGuard: false,
            DisableCopilotPeekScenarioNameUpdate: false,
            DisableRemovingShareAsNewsletterForSydneyStatusReport: false,
            DisableDefaultExpandedOptionsForSydneyStatusReport: false,
            DisableStatusReportExtensionInitializationAfterGeneration: false,
            DisableNotRenderingCopilotFeedbackForStatusReport: false,
            DisableEnableExportToExcelForInactiveProjects: false,
            DisableOmitClientOperationId: false,
            DisableForceAuthRefreshOnInactivity: false,
            DisableAuthInteractionRequiredUsingPopup: false,
            DisableSsoSilentRedirectFallback: false,
            DisableBootstrapAuthPrefetchAndCAEvaluation: false,
            DisableBootstrapCaeProbe: false,
            DisableBootstrapCaeProbeFetchPlan: false,
            DisableRemoveChannelFetchOnMyPlansList: false,
            DisableRosterPlansPagination: false,
            DisableGoalsPrefetch: false,
            DisableParseJsonResponseHelper: false,
            DisableLicenseCheckForGetPortfoliosCall: false,
            DisablePortfoliosPlanLinkRedirectionFix: false,
            DisableCp1ClientCapabilityWeb: false,
            DisableEvoPlanContextRefresh: false,
            DisableConditionalColoringChannelMappingFixes: false,
            DisablePoMtacColumnReorderFix: false,
            DisableDefaultModalCloseOnNavigation: false,
            DisableRecommendedPlanFetchLimit: false,
            DisableUserSearchQueryFix: false,
            DisableStoringFailedContainerFetch: false,
            DisableGroupDetailsLoopFix: false,
            DisableRefreshEnterpriseDataErrorBannerRemovalForReadonlyUser: false,
            DisableEtccDialogCheckboxStateFix: false,
            DisableSendOnPlanOrPortfolioSettingReadFromGraph: false,
            DisableBasicPlanPageViewACFix: false,
            DisableFetchGroupWithoutAutoSubscribeField: false,
            DisableStopBackgroundSyncWhenCached: false,
            DisableDeferredBootForBackgroundLoad: false,
            DisableTasksPlatformClientUsageHeader: false,
            DisablePreFetchTasksPlatformClientTypeHeader: false,
            DisableUnoNotificationsRework: false,
            DisableChineseTraditionalTemplateImageSwap: false,
            DisableTemplateDisabledList: false,
            DisableInitializeLandingPageCultureDataOutsideBootPath: false,
            DisableTodoNotesQuickLookBlock: false,
            DisableTaskEditorSetTaskStateOptimization: false,
            DisablePublishingFRE: false,
            DisableLoginRetryWithoutPrompt: false,
            DisableRenderMyPlansListWithoutBasicPlanStoreInitialization: false,
            DisableClientDrivenSessionId: false,
            DisableAddAttachmentFlowForSharedChannel: false,
            DisableTeamsChannelFetchRefactor: false,
            DisablePlannerCrudFetchDedup: false,
            DisablePlannerCrudFetchUtility: false,
            DisableMruDeleteHydrateFix: false,
            DisableBrowserUpgradeBanner: false,
            DisableInlineLegacyCommentsInTaskEditor: false,
            DisableServiceThrottling: false,
            DisableGraphServiceThrottling: false,
            DisableEvoServiceThrottling: false,
            DisableTeamsTabTenantIdInWebsiteUrl: false,
            DisableTeamsTabContextLinkInPlaceUpdate: false,
            DisableCallStackInUnhandledErrorLog: false,
            DisablePreSendTelemetryTruncation: false,
            DisableGridEditIsolation: false,
            DisableTaskScheduleModeInHistory: false,
            DisableTaskTypeUpdatingSpinner: false,
            DisableTaskIgnoreResourceCalInHistory: false,
            DisableTaskIgnoreResourceCalUpdatingSpinner: false,
            DisableMsalV2: false,
            DisableExpandCollapseFastPath: false,
            DisableAuthCircuitBreaker: false,
            DisableSubscriptionStatusCircuitBreaker: false,
            DisableChatDeltaSync: false,
            DisableStaleDeltaTokenFullFetchFallback: false,
            DisableTaskAndChatPreFetch: false,
            DisableInactivityGate: false,
            DisableCustomFieldSubmitValueRef: false,
            DisableSuppressBackgroundSyncTransportFailureNotification: false,
            DisablePreserveUserPhotoFetchStatus: false,
            DisableModernGroupPhotoFetchOptimization: false,
            DisableUserPhotoDataUri: false,
            DisableClientOperationIdHeader: false,
            DisableMruIdInWritePayloads: false,
            DisableMruPinStatusInNonPinUpdates: false,
            DisableMruFetchPinTimestampReconciliation: false,
            DisableViewLifecycleForceFetchOrderingFix: false,
            DisablePlannerXCorrelationId: false,
            DisableLogPlannerXRefHeaders: false,
            DisableSkipTrouterSubscriptionInBackground: false,
            DisableTrouterForegroundCatchUp: false,
            DisableTaskChatComposeDraft: false,
            DisableGroupCreationMembershipCheckFix: false,
            DisableAccurateGroupCreationForbiddenMessage: false,
            DisablePublishedTaskCommentCreatedDateFix: false,
            DisablePlanSubroutePersistence: false,
            DisableUnresolvedDeepLinkDefaultRoute: false
        }; // any new CG should be false by default
    }
    /**
     * Get the flights with the default values.
     */ static getDefaultFlights() {
        return {
            EnableHCU: false,
            EnableGoalsOKRFields: false,
            EnableFilterPjoFromRecentPlans: false,
            EnableMandatoryForms: false,
            EnableNLPInTaskCreation: false,
            EnableMandatoryApprovals: false,
            EnableMandatoryChecklist: false,
            EnableUpsellConversion: false,
            EnableEnhancedLoopRenderingErrorHandling: false,
            EnableFirstRunExperience: false,
            EnableUnoLevelProjectProvisioning: false,
            EnableUnoLevelPortfolioProvisioning: false,
            EnableP4WDeprecationChanges: false,
            EnableRecurrenceForTaskEachAssignee: false,
            EnableRecurrenceList: false,
            EnableRecurrenceListEditRecipients: false,
            EnablePortfolioExperience: false,
            EnableWhiteBoardInPremiumPlan: false,
            EnableTeamsTabPlanContextRefresh: false,
            EnablePersistenceForTodoTasks: false,
            EnableGSDInPremiumPlan: false,
            EnableBasicPlanParallelConversion: false,
            EnableAppPoweredTasks: false,
            EnableTaskEditorRouting: false,
            EnableTaskEditorPanel: false,
            EnableGSDHome: false,
            EnableGSDHomeResources: false,
            EnableGSDHomeResourcesEsdkPicker: false,
            EnableSharepointGrounding: false,
            EnableWebGrounding: false,
            EnableIncrementalLoadInPremiumPlans: false,
            EnablePublishingNewListSelector: false,
            EnablePerfImprovementPeopleBoardPage: false,
            EnableCopilotMyDayMyTasks: false,
            EnablePremiumPlansInCreateTaskFromMessage: false,
            EnableCapabilitiesCheck: false,
            EnableBulkEditInBasicPlan: false,
            EnableProjectTasksForPlannerWeb: false,
            EnableSuggestedAssignee: false,
            EnableBaselineWave2: false,
            EnablePremiumPlanStartTrialEntryPoints: false,
            EnableDeletePortfolioExperience: false,
            EnableFlowAuthenticationWithTeamsSDK: false,
            EnableGccHInP4W: false,
            EnableCatalystIntegration: false,
            EnableCatalystLoaderSSR: false,
            EnableCopilotDAB: false,
            EnableCopilotDabDefaultExpanded: false,
            EnableUnifiedCopilotIcon: false,
            EnableFrontierTagForCopilotPane: false,
            EnablePlannerAgentFiltering: false,
            EnableCatalystForMyTasks: false,
            EnableDynamicFilterPersistence: false,
            EnableErrorBoundaries: false,
            EnableCanvasTaskGrid: false,
            EnableCanvasGoalTasksGrid: false,
            EnableSinceRevisionToken: false,
            EnableExpandCollapseSummaryTask: false,
            EnablePMCorrectionExperience: false,
            EnableProjectTasksInPlanner: false,
            EnableNewProjectDialogAndSurveyForUsersRoutedFromPJOBanners: false,
            EnableCopilotPeek: false,
            EnableGsdInBasicPlans: false,
            EnableWebHeaderChanges: false,
            EnableGraphV1ForDriveItemShareInvitation: false,
            EnablePermissionsV2ForDriveItem: false,
            EnableAIGeneratedTaskAttribution: false,
            EnableUnifiedDiscoveryEndpointForProjectAndRoadmap: false,
            EnableDataPersistence: false,
            EnableTasksDataPersistence: false,
            EnableCacheEvictionPolicies: false,
            EnableUsingAppIdForAuthInPexTeamsApp: false,
            EnableDataPreFetcher: false,
            EnablePersistentFiltersInPremiumPlans: false,
            EnableInstantiatePremiumPlanFromWorker: false,
            EnableLegacyTTServiceWorkerCleanup: false,
            EnableStandardizedErrorContractForPremiumPlans: false,
            EnablePlannerPromotionalBanner: false,
            EnablePlannerPromotionalBannerForRoadmap: false,
            EnableTabPlannerPromotionalBanner: false,
            EnablePlannerPromotionalBannerForRoadmapTeams: false,
            EnableCp1ClientCapabilityInTeams: false,
            EnableKeepExistingTasksOnRegeneratingTasksByCopilot: false,
            EnableTaskCompletionInProjOps: false,
            EnableGSDReadOnlyView: false,
            ShowNamedOrgPlansInUno: false,
            EnableDataFetchViaWorker: false,
            EnableWhiteboardInBasicPlan: false,
            EnableBlockerUIInDoDInP4W: false,
            EnableCopilotCIQGrounding: false,
            EnableShowHideInMyDayAndMyTasks: false,
            EnableTeamsNotifySuccessV2: false,
            EnableReportPageInPremiumPlan: false,
            EnableMeetingPlanExpiration: false,
            EnableTeamsActivityFeedNotificationTelemetry: false,
            EnableInAppFeedbackVNext: false,
            EnableOcdiRedirectBanner: false,
            EnableConversationButtonInGCCH: false,
            EnableWorkerDeferredInit: false,
            EnableAsyncDispatchV2: false,
            EnableHidingGSDInBasicPlansBasedOnTenantSetting: false,
            EnablePmaInAllViews: false,
            EnableGsdInMeetingPlans: false,
            EnableMeetingTaskCitation: false,
            EnableContentCreationTemplate: false,
            EnableGoalsInPlan: false,
            EnableGoalsInBoardView: false,
            EnableGoalsInGridView: false,
            EnableGoalsInBasicPlan: false,
            EnableGoalsInWorkbackPlan: false,
            EnableEvoPMA: false,
            EnableEvoPLink: false,
            EnablePremiumCapabilitiesInGrid: false,
            EnableGoalsTaskRowGutter: false,
            EnableEvoCalendar: false,
            EnableEvoTime: false,
            EnablePremiumPlanTaskSuggestionsFab: false,
            EnablePremiumPlanTaskSuggestions: false,
            EnableNewTemplateIds: false,
            EnableCopilotInBasicPlan: false,
            EnableGroundingInBasicPlanGoals: false,
            EnableGsdInExistingPremiumPlans: false,
            EnableTokenEncryption: false,
            EnableFabAndGoalsFeaturesInNonGroupPlans: false,
            EnableCopilotNotificationInDialog: false,
            EnableFormerUserNotification: false,
            Enable3PAgentsSupport: false,
            EnablePlanInTeamsChannel: false,
            EnableBasicPlanTaskChatMentions: false,
            EnableBasicPlanCustomTemplate: false,
            EnableStatusReportForNonEnglishLocale: false,
            EnableDeleteForStatusReport: false,
            EnableIsStarredTaskProperty: false,
            EnableStarredExperience: false,
            EnableShareAsNewsletterForStatusReport: false,
            EnableExtensionsForStatusReport: false,
            EnableKeepItDiscardForStatusReport: false,
            EnablePrivatePlanSupportForStatusReport: false,
            EnableNewPastReportSection: false,
            EnableStatusReportGenerationViaPlannerAgent: false,
            EnableSensitivityLabelInPMA: false,
            EnableSchedulingIntegration: false,
            AddPreferHeaderToGraphRequest: false,
            EnableMigrateLegacyToUnoTeamsTabs: false,
            EnableEmailNotificationsStoredInGraph: false,
            EnableSubmitPremiumTaskWorkflowAPI: false,
            OmitCredentialsWithAuthToken: false,
            EnableProjectEOLScreen: false,
            EnableFluentUIChartsInBasicPlans: false,
            EnableDeferDataFetchForBackgroundLoad: false,
            EnableNewAttachedResourcesCallout: false,
            EnableServiceWorker: false,
            EnableErrorMessageLogging: false,
            EnableMruServicePostRetries: false,
            EnableMruUpsertKeepalive: false,
            EnableFetchAllMembersAPIForTeamsChannel: false,
            EnableTeamsChannelContainerAccessControl: false,
            EnableGoalsNameInTaskEditor: false,
            EnableCreateGoalsWithAI: false,
            EnableTriggeringMetaOSDataPreFetcherFromBootstrap: false,
            EnableCustomTaskViewInProjOps: false,
            EnableContentLevelSensitivityLabels: false,
            EnableSignalRConnectionJitter: false,
            EnablePlannerAsATabInSharedChannel: false,
            EnablePlannerAsATabInPrivateChannel: false,
            EnableAddExistingPlanInSharedChannelTab: false,
            EnableStatusReportsForEvoPlans: false,
            EnableContentLevelSensitivityLabelsEvo: false,
            EnableApplyDefaultContentLevelSensitivityLabel: false,
            EnablePersistenceEvo: false,
            EnableCacheEvictionEvo: false,
            EnableLoopIntegrationInMeetingPlans: false,
            EnableUpsellExperienceEvo: false,
            EnableServiceWorkerInEvo: false,
            EnableDependencies: false,
            EnableDependenciesWithScheduling: false,
            EnableOutlineInAssignmentsGrid: false,
            EnableTrouterIntegration: false,
            EnableTrouterChatEchoSuppression: false,
            EnableTrouterChangeMetadata: false,
            EnableUserSessionIdHeader: false,
            EnableMultiTaskMove: false,
            EnableInvalidPlannerIdRequestSuppression: false,
            EnableEffortRemainingUpdateInProjOps: false,
            EnableUserBlockDeleteColumn: false,
            EnableTaskHistoryInfoTooltip: false,
            EnableTaskHistory: false,
            EnableCustomFields: false,
            EnableCustomDateRangeFilter: false,
            EnableCustomFieldChoiceFirstPartyFields: false,
            EnableCustomFieldsInCustomTemplates: false,
            EnableAssignmentGridConfigurableTimescaleInProjOps: false,
            EnableV9PlanPicker: false,
            EnablePersonalViewOrderHintPrecisionFix: false,
            EnableTimelineViewEvo: false,
            EnableTeamViewEvo: false,
            EnableUsageBasedLabels: false,
            EnableViewConfigMigration: false,
            EnableBucketColors: false,
            EnableTaskFormatOrderHints: false,
            EnableNewMyTasksExperience: false,
            EnableTaskFilterResultCount: false,
            EnableAlwaysVisibleGridTaskCount: false,
            EnableIwmCompactCard: false,
            EnableMyTasksDefaultPlan: false,
            EnableEvoPMAInRosterPlans: false,
            EnableCopyPlanInRosterPlans: false,
            EnableSpoAuthForPublishing: false,
            EnableAuthenticatedMsalV2: false,
            EnableUnauthenticatedMsalV2: false,
            EnableCustomFieldsRollup: false,
            EnableExpandedCustomFields: false,
            EnableCanvasGridCellCopyPaste: false,
            EnableCanvasGridTaskCopyPaste: false,
            EnablePlannerServiceWebuiEntry: false,
            EnableTaskHierarchy: false,
            EnableCrudUtilities: false,
            EnableSkipAuthUpn: false,
            EnableUndoRedoInEvoPlans: false,
            EnableCellPasteUndo: false,
            EnablePercentComplete: false,
            EnableMyTasksIntraGroupReorder: false,
            EnableIndependentTaskDueDate: false,
            EnableConditionalColoringEvo: false,
            EnablePlannerServiceEntitlementsAndUpsellAPI: false,
            EnablePlannerEntitlementsShadowDeployment: false,
            EnableUpsellWave3Flow: false,
            EnableEtccCurrencyAndLookup: false,
            EnableUpdateConflictRetry: false,
            EnableBulkEditInProjOps: false,
            EnableChunkedBulkTaskUpdate: false,
            EnableTimelineDueDateIndicator: false,
            EnableTaskChatEditorRoosterV9: false,
            EnableSendAdaptiveCardViaGraph: false,
            EnableEvoAppRefresh: false,
            EnableMultipleBaselines: false,
            EnableDirtyBitCheck: false,
            EnableUpdateWithRetry: false,
            EnableEtagRollbackReset: false,
            EnableDeleteWithRetry: false,
            EnableDefaultTaskColumnConfiguration: false,
            EnableColumnAccessETCC: false,
            EnableCustomStatus: false,
            EnableEmojiStore: false,
            EnableTaskNotesRibbon: false,
            EnableTaskNotesImages: false,
            EnableBlockedStatus: false,
            EnableFloodgateDynamicCampaign: false,
            EnableFiltersInGoalsView: false,
            EnableUndoOfOptimisticOperation: false,
            EnableTotalEffort: false,
            EnableCustomViews: false,
            EnableCustomViewsLocalStorage: false,
            EnableUnDeleteEntity: false,
            EnableTemplatesInSharedAndPrivateChannels: false,
            EnableSprints: false
        };
    }
    static getDefaultSettings() {
        return {
            ConfigIDs: "configId",
            ETag: "eTag",
            Version: "",
            unoConfig: {
                version: "",
                teamsTasksAriaInstrumentationKey: ""
            },
            rolloutStage: RolloutStage.ProdAll,
            clientBasicLimits: {
                maxAttachmentsOnTask: 10,
                maxDisplayablePersonasTaskCard: 3,
                maxDisplayablePersonasAddTaskCard: 4,
                maxDisplayablePersonasTaskDetails: 8,
                maxTaskAssignees: 20,
                maxUserContainerPlansLimit: 200,
                maxCustomViews: MAX_CUSTOM_VIEWS_COUNT,
                maxRequiredFormsOnTask: 2,
                maxRequiredApprovalsOnTask: 1,
                maxEditTaskLimit: 20,
                maxUsersSharedWithPlan: 100,
                maxExternalAgentLimit: 6,
                maxCategoryNameLength: 100,
                maxNotesLength: 32768,
                maxChecklistItemsPerTask: 20,
                maxReactionsPerMessagePerUser: 5,
                maxTaskHierarchyDepth: 5,
                maxTaskSubtreeSize: 50
            },
            auth: {
                authTokenExpiryBufferSeconds: 300,
                msalSettings: null,
                circuitBreakerThreshold: 5,
                circuitBreakerCooldownMs: 60000
            },
            instantiation: {
                maxRetryCount: 10,
                retryIntervalMs: 500
            },
            audienceGroup: AudienceGroup.Production,
            serviceConfigurations: {
                auth: {
                    hostname: "https://tasks.teams.microsoft.com",
                    authResourceURI: "https://tasks.teams.microsoft.com"
                },
                augloop: {
                    hostname: "https://augloop.office.com",
                    authResourceURI: "https://augloop.office.com/v2"
                },
                centro: {
                    hostname: "https://admin-ignite.microsoft.com",
                    authResourceURI: ClientAppId.CentroService
                },
                chatsvcagg: {
                    hostname: "https://chatsvcagg.teams.microsoft.com",
                    authResourceURI: "https://chatsvcagg.teams.microsoft.com"
                },
                copilotEligibility: {
                    hostname: "https://mss.office.com",
                    authResourceURI: ClientAppId.Olympus
                },
                forms: {
                    hostname: "https://forms.office.com",
                    authResourceURI: "https://forms.office.com"
                },
                flow: {
                    hostname: "https://api.flow.microsoft.com",
                    authResourceURI: "https://service.flow.microsoft.com",
                    widgetBaseUri: "https://make.powerautomate.com",
                    portfolioTemplateId: "698e0dd9a2dc4ae19b30b73ee5074303"
                },
                graph: {
                    hostname: "https://graph.microsoft.com",
                    authResourceURI: "https://graph.microsoft.com",
                    batchAll: false,
                    maxBatchSize: 20,
                    batchInterval: 100
                },
                iris: {
                    hostname: "https://arc.msn.com",
                    authResourceURI: "https://arc.msn.com/v4",
                    placementId: IrisSurfacePlacementIds.PlannerExternal,
                    batchCount: 3
                },
                mars: {
                    hostname: "https://taskmars.microsoft.com",
                    authResourceURI: "3b3a0f1f-2a1c-4805-9e05-43a40c630002",
                    timeout: 240000
                },
                loop: {
                    hostname: "https://prod.api.loop.cloud.microsoft/v0.1",
                    hostnameEu: "https://eu.prod.api.loop.cloud.microsoft/v0.1",
                    authResourceURI: "https://api.loop.cloud.microsoft",
                    timeout: 70000
                },
                loopMts: {
                    hostname: "https://substrate.office.com",
                    authResourceURI: "https://substrate.office.com"
                },
                mru: {
                    hostname: "https://ocws.officeapps.live.com",
                    hostnameEu: "https://ocws-eu.officeapps.live.com",
                    officeApplicationNumber: "153",
                    authResourceURI: "https://api.office.net",
                    timeout: 30000,
                    slowUpsertDiagnosticsThresholdMs: 30000
                },
                planner: {
                    hostname: "https://tasks.office.com",
                    authResourceURI: ClientAppId.PlannerService
                },
                plannerWeb: {
                    hostname: "https://planner.cloud.microsoft",
                    authResourceURI: ClientAppId.MicrosoftPlannerClient
                },
                project: {
                    hostname: "https://project.microsoft.com",
                    authResourceURI: "https://project.microsoft.com",
                    cdsEndpointTimeout: 30000,
                    planConversionTimeout: 120000,
                    acquireTrialTimeout: 30000,
                    fetchSubscriptionStatusTimeout: 30000,
                    createPortfolioTimeout: 30000
                },
                ocps: {
                    hostname: "https://clients.config.office.net",
                    authResourceURI: "https://clients.config.office.net"
                },
                odsp: {
                    hostname: "",
                    authResourceURI: ClientAppId.Odsp
                },
                oneshell: {
                    hostname: "https://webshell.suite.office.com",
                    authResourceURI: "https://webshell.suite.office.com",
                    shellBootHost: "https://res-1.cdn.office.net",
                    shellBootPath: "/shellux/api/shellbootstrapper/business/oneshell"
                },
                roaming: {
                    hostname: "https://roaming.officeapps.live.com",
                    hostnameEu: "https://roaming-eu.officeapps.live.com",
                    officeApplicationNumber: "153",
                    authResourceURI: "https://api.office.net"
                },
                tcms: {
                    hostname: "https://metadata.templates.cdn.office.net",
                    authResourceURI: ""
                },
                retailservices: {
                    hostname: "https://tasks.teams.microsoft.com",
                    authResourceURI: "https://retailservices.teams.microsoft.com",
                    appFlavor: "planner"
                },
                stapi: {
                    hostname: "https://substrate.office.com",
                    authResourceURI: "https://substrate.office.com"
                },
                substrateSearch: {
                    hostname: "https://substrate.office.com",
                    authResourceURI: "https://substrate.office.com/search"
                },
                todo: {
                    hostname: "https://to-do.office.com",
                    authResourceURI: "",
                    onlineTxtEndpoint: "https://res-1.cdn.office.net/todo/online.txt"
                },
                outlook: {
                    hostname: "https://outlook.office.com",
                    authResourceURI: ""
                },
                teams: {
                    hostname: "https://teams.microsoft.com",
                    authResourceURI: "https://teams.microsoft.com",
                    cdnResourceURI: "https://statics.teams.cdn.office.net",
                    emojiMetaDataId: "86e6062e3e6843b4b62bd03d55440544"
                },
                trouter: {
                    hostname: "https://go.trouter.teams.microsoft.com",
                    hostnameEu: "https://go-eu.trouter.teams.microsoft.com",
                    authResourceURI: ClientAppId.TrouterPlannerIntegration
                }
            },
            retrySettings: {
                globalAjaxTimeout: 15000,
                maxRetryCount: 3,
                defaultRetryDelay: 1000,
                maxRetryTimeout: 20000,
                maxUpdateRetries: 3,
                maxDeleteRetries: 3
            },
            premiumPlanCreationStatusPollSettings: {
                maxPollCount: 10,
                baseInterval: 1000
            },
            taskHistoryCatchUpPollSettings: {
                maxPollCount: 3,
                baseInterval: 500
            },
            batchJobSizes: {
                fetchUsers: 10,
                fetchPhotos: 20,
                addUsersToContainer: 15,
                fetchUserGroupPlansPageSize: 30,
                fetchMeetings: 10,
                plannerServiceParallelRequests: 5
            },
            deltaSyncConfiguration: {
                baseInterval: 6,
                cutoff: 60,
                backoffThreshold: 5,
                retryInterval: 300
            },
            fileUpload: {
                maxFileSize: 10485760,
                maxUploadRetries: 3,
                uploadChunkSize: 327680,
                uploadTimeoutMs: 30000
            },
            localDevelopmentSettings: {
                showHiddenToasts: false
            },
            templateSettings: {
                // Default to production lists, as these are a subset of all possible built-in templates. Pre-prod environments will
                // be configured to show the PPE list of templates, as these templates might have localization issues or be broken when
                // first added to the template management service (and want to avoid breaking creation flows where possible).
                basicPlanTemplateList: TemplateListType.BasicPlanProduction,
                premiumPlanTemplateList: TemplateListType.PremiumPlanProduction,
                planTemplateList: TemplateListType.PlanProduction,
                // Timeout for the custom template search request. Kept configurable via ECS since this call
                // (Graph search over driveItems) has shown elevated latency/failure rates in some environments.
                templateSearchTimeoutInMilliseconds: 40000,
                // The disabled list of templates per locale
                disabledTemplateList: [
                    {
                        locale: "zh-tw",
                        templateIdList: [
                            TemplateIdConstants.ProjectManagementPremiumPlanTemplateId,
                            TemplateIdConstants.CommercialConstructionPremiumPlanTemplateId,
                            TemplateIdConstants.SoftwareDevelopmentPremiumPlanTemplateId,
                            TemplateIdConstants.BusinessPremiumPlanTemplateId,
                            TemplateIdConstants.EmployeeOnboardingPremiumPlanTemplateId,
                            TemplateIdConstants.SimplePremiumPlanTemplateId,
                            TemplateIdConstants.MarketingCampaignPremiumPlanTemplateId
                        ]
                    }
                ]
            },
            sharedContainers: {
                updateDriveItemLinksToOpenOnWeb: false
            },
            oneDSSinkEnvironment: OneDSEnvironment.Public,
            disableImageRecommendations: false,
            filterGuestUsersOnTenantSearch: false,
            userSearchResultsExpiryTimeInSeconds: 30,
            showFirstRunExperienceAfter: new Date("2024-01-01T00:00:00.000Z"),
            applicationIds: {
                project: "f53895d3-095d-408f-8e93-8f94b391404e"
            },
            disableFileUploads: false,
            disableTaskChat: false,
            basicPlanConversionSettings: {
                overallTimeoutDuration: 90000,
                loopInterval: 2000,
                maxCount: 50
            },
            forceLogsToEU: false,
            disableStoragePersistence: false,
            disableAppCaching: false,
            disableTeamsAppBackgroundLoad: false,
            usePointerBackendInDnd: false,
            disablePreFetcher: true,
            taskPublishingSettings: {
                disablePublishingFreCallouts: false
            },
            routePersistenceEnabled: false,
            irisCampaignSettings: {
                disableIrisCampaignCallouts: false,
                campaignCacheExpiryDuration: 14400000
            },
            maxConversationCreationPeriod: 300,
            AugloopFlights: "",
            CopilotAugloopFlights: "",
            PortfolioMinCDSVersionSupported: "0.8.8.60",
            persistedStorage: {
                limits: {
                    maxTaskFilters: 20,
                    maxSortCriteria: 20,
                    maxGridColumnWidths: 20,
                    maxTaskGroupings: 20
                },
                expiryTimes: {
                    cdsInstanceData: 300,
                    cdsPackageInfoData: 300
                }
            },
            traceLogThreshold: TraceLevel.Info,
            blockedTraceTags: [],
            workerSettings: {
                bootObserverWorker: {
                    timeout: 20000
                },
                undoRedoWorker: {
                    cleanupIntervalMs: 10 * 60 * 1000,
                    entryTtlMinutes: 30
                },
                cdsProvisioningStatusPollingWorker: {
                    maxPollCount: 60,
                    baseInterval: 30000
                },
                recommendedPlansWorker: {
                    disabled: false,
                    recommendedPlansUpdateInterval: 1000 * 60 * 20,
                    recommendedPlansLimit: 10,
                    recommendedPlansFetchLimit: 140
                },
                operationTrackingWorker: {
                    disabled: false,
                    defaultProcessingInterval: 60,
                    defaultOperationExpiryDuration: 60 * 60 * 24 * 1,
                    copyPlan: {
                        processingInterval: 5,
                        operationExpiryDuration: 60 * 60 * 24 * 1
                    },
                    generateStatusReport: {
                        processingInterval: 30,
                        operationExpiryDuration: 60 * 60 * 1 * 1
                    },
                    premiumPlanTaskSuggestion: {
                        processingInterval: 5,
                        operationExpiryDuration: 60 * 60 * 6
                    },
                    createPlanFromTemplate: {
                        processingInterval: 5,
                        operationExpiryDuration: 60 * 60 * 24 * 1
                    },
                    sydneyStatusReport: {
                        processingInterval: 30,
                        operationExpiryDuration: 60 * 20
                    }
                },
                cacheUpdateWorker: {
                    cacheUpdateInterval: 2000,
                    operationRetryAttempts: 3
                },
                cacheEvictionWorker: {
                    noOfPlansCachedThreshold: 20,
                    plansRecencyThreshold: 30,
                    userPhotosTtlInDays: 1,
                    modernGroupPhotosTtlInDays: 1,
                    usersTtlInDays: 2
                }
            },
            resourceTelemetrySettings: {
                enableResourceTelemetryCollection: false,
                enableLoggingLinkResourceEntries: false,
                enableLoggingScriptResourceEntries: false,
                enableLoggingFetchResourceEntries: false
            },
            performanceSettings: {
                minimumTasksForVisualReadiness: 100,
                minimumPlansForVisualReadiness: 100,
                longTaskDurationLoggingThreshold: 1000,
                longAnimationFrameDurationLoggingThreshold: 1000,
                enableLatencyTracking: false,
                cls: false,
                lcp: false,
                inpTracking: {
                    worstMode: false,
                    thresholdMode: false,
                    durationThreshold: 50,
                    loafEntries: 3,
                    loafScriptEntries: 5,
                    aggregateMode: false,
                    aggregateKeydownThreshold: 50,
                    aggregateClickThreshold: 50,
                    maxWalkDepth: 3
                }
            },
            copilotExecutionSettings: {
                maxActiveTasks: 1,
                activeTaskExpiryTimeInSeconds: 300
            },
            copilotSettings: {
                agentLoadTimeoutMs: CopilotAgentLoadTimeoutMs,
                agentLoadMaxRetries: CopilotAgentLoadMaxRetries
            },
            MaxTaskLimitForPremiumPlans: 3000,
            EnforceOCPS: false,
            OfficeCustomerVoiceEnabled: true,
            premiumPlanReportSettings: {
                showPastReports: false,
                showMessageBarMoreOptions: false,
                shouldUseSPOStorage: false,
                shouldUseAsyncFlow: false,
                showSharepointNewsletter: false
            },
            EnvironmentFeatureSettings: {
                EnablePortfolioExperience: true,
                EnablePremiumPlanExperience: true,
                EnableProjectManagerExperience: true,
                EnableTasksPublishingExperience: true,
                EnableTodoExperience: true,
                EnableTemplateExperience: true,
                EnableONotifyExperience: true
            },
            evoPersistentAsyncStorageVersion: 1,
            persistentAsyncStorageVersion: 1,
            persistenceSettings: {
                route: false,
                filters: false,
                mru: false,
                tasks: false,
                view: false
            },
            evoPersistenceSettings: {
                mru: false,
                tasks: false,
                users: false,
                view: false,
                userPhotos: false,
                modernGroupPhotos: false,
                maxViewConfigEntries: 20,
                chat: false,
                maxPlanCalendarNudgeDismissedPlanIds: 50,
                planCalendarNudgeDismissalDurationMs: 259200000
            },
            publishingSettings: {
                pageSize: 250,
                persistenceDebounceMs: 1000
            },
            tabSettings: {
                disableTabStorage: false
            },
            GSDInBasicPlanSettings: {
                enablePMAInMyDayAndMyTasksBoardView: false
            },
            disableDataverse: false,
            disableServiceWorker: false,
            disableICalendar: false,
            enablePrivacyGuard: false,
            chatSettings: {
                messageRefreshThresholdInSeconds: 60,
                chatDeltaSyncSettings: {
                    disable: false,
                    syncInterval: 60,
                    cutoff: 300,
                    failureBackoffInterval: 60,
                    minEmptyStreakThreshold: 0
                }
            },
            contentLevelSensitivityLabelsContainerType: [],
            applyDefaultSensitivityLabelAfter: new Date("2026-02-23T00:00:00.000Z"),
            deltaSyncSettings: {
                disable: false,
                syncInterval: 6,
                cutoff: 60,
                failureBackoffInterval: 6,
                minEmptyStreakThreshold: 0
            },
            inactivityThresholdMs: 300000,
            disableServiceWorkerInEvo: false,
            idValidationSettings: {
                disableRequestSuppression: false,
                plannerIdlength: 28
            },
            stalenessSettings: {
                disable: false,
                defaultTtl: 300,
                entityOverrides: {
                    Plan: 60
                },
                failedRequestRetryWindowInSeconds: 30
            },
            trouterSettings: {
                disable: false,
                subscriptionTtlMs: 3600000,
                trouterModeDeltaSyncIntervalInSeconds: 600,
                notificationDebounceBufferIntervalMs: 200,
                disableResubscribeOnDelta: true,
                connectionStateDebounceMs: 60000,
                chatEchoSuppressionRecencyWindowMs: 15000,
                verboseSdkLogging: false,
                telemetry: {
                    enabled: true,
                    // The four events the Trouter team relies on to reason about client health
                    connected: true,
                    disconnected: true,
                    error: true,
                    progress: true,
                    // Mostly deprecated on the Trouter side and by far the highest volume, so off by default
                    response: false,
                    request: false,
                    registration: false,
                    unregistration: false,
                    checkConnection: false,
                    numberOfStepsToMaintain: 50,
                    sendProgressTimeoutSecs: 300,
                    logHealthCheckError: true,
                    logSendPingError: true
                }
            },
            storePublishFrequencyMonitorSettings: {
                windowMs: 1000,
                threshold: 50
            },
            throttleSettings: {
                maxConcurrentRequests: 10,
                circuitBreakerThreshold: 3,
                circuitBreakerPauseMs: 10000,
                maxCircuitBreakerPauseMs: 60000,
                queueTimeoutMs: 30000
            },
            requestDedupSettings: {
                getWindowMs: 0
            },
            dependencySettings: {
                planDependencyLimit: 2000,
                taskDependencyLimit: 20
            },
            sydneyStatusReportSettings: {
                timeout: 15 * 60
            },
            customFields: {
                maxFieldsPerPlan: 10,
                minChoicesPerField: MinChoicesPerCustomField,
                maxChoicesPerField: MaxChoicesPerCustomField,
                maxFieldNameLength: MaxCustomFieldDefinitionNameLength,
                maxChoiceNameLength: MaxCustomFieldChoiceNameLength,
                maxTextValueLength: MaxTextCustomFieldValueLength,
                maxNumberValue: MaxNumberCustomFieldValue,
                minNumberValue: MinNumberCustomFieldValue
            }
        };
    }
    /**
     * Builds IAppContextSettings based on given settings getter delegate function.
     * @param settingsGetter delegate function that returns settings value given a settings key string
     * @returns The instance of IAppContextSettings that is built from given settingsGetter
     */ static BuildContextSettingsFromSettingsGetter(settingsGetter) {
        const ecsConfig = settingsGetter(SettingsConstants.Config);
        const [ecsFlights, ecsChangeGates, oldChangeGates, ecsSettings] = AppContext.getFlightsCGsAndSettings(ecsConfig);
        const serverSessionMetaData = settingsGetter(SettingsConstants.SessionMetaData);
        if (!serverSessionMetaData) {
            throw new ConfigNotFoundError(SettingsConstants.SessionMetaData);
        }
        const sessionMetaData = AppContext.getSessionMetaData(serverSessionMetaData);
        const runtimeConfig = {
            activeWebUrlPrefix: WebUrlSegments.WebUI
        };
        const appSettings = {
            appConfiguration: {
                flights: ecsFlights,
                changeGates: ecsChangeGates,
                oldChangeGates: oldChangeGates,
                settings: ecsSettings,
                sessionMetaData: sessionMetaData,
                runtimeConfig: runtimeConfig
            }
        };
        return appSettings;
    }
    /** Current App Configuration */ static get appConfiguration() {
        return AppContext._appConfiguration;
    }
    /** Method to override the client flavor. Used for cached app load in Teams */ static overrideClientFlavor(clientFlavor) {
        // Right now we'll block on Teams. Important note is other MOS hosts will be able to override this.
        if (AppContext._appConfiguration.sessionMetaData.appMetadata.clientType !== ClientType.Teams) {
            throw new Error("Client flavor can only be overridden for Teams client type");
        }
        AppContext._appConfiguration.sessionMetaData.appMetadata.clientFlavor = clientFlavor;
    }
    /** Sets the runtime config. Called by WebAppBootstrap after detecting browser-specific values. */ static setRuntimeConfig(runtimeConfig) {
        AppContext.appConfiguration.runtimeConfig = runtimeConfig;
    }
    /**
     * Returns true if the environment is a PPE one. Returns false otherwise.
     */ static isPreProductionEnvironment() {
        return AppContext.appConfiguration.sessionMetaData.environment === Environment.Ppe;
    }
    /**
     * Returns true if the environment is gov environment. Returns false otherwise.
     */ static isGovEnvironment() {
        return AppContext.appConfiguration.sessionMetaData.environment === Environment.Gcc || AppContext.appConfiguration.sessionMetaData.environment === Environment.Gcch || AppContext.appConfiguration.sessionMetaData.environment === Environment.Dod || AppContext.appConfiguration.sessionMetaData.environment === Environment.Agc;
    }
    /** Checks if the combination of Rollout Stage and Environment is unexpected, and logs unexpected entries. */ static isRolloutStageAndEnvironmentCombinationUnexpected() {
        const rolloutStage = AppContext.appConfiguration.settings.rolloutStage;
        const environment = AppContext.appConfiguration.sessionMetaData.environment;
        const allowedRolloutStages = allowedRolloutStagesForEnvironment.get(environment);
        return !allowedRolloutStages?.includes(rolloutStage);
    }
    /** Returns an IFlight object and an ISettings object from the config object returned from ECS. */ static getFlightsCGsAndSettings(ecsConfig) {
        const flights = AppContext.getDefaultFlights();
        const flightKeys = Object.keys(flights);
        for (const flightKey of flightKeys){
            if (ecsConfig?.[flightKey] != null) {
                flights[flightKey] = ecsConfig[flightKey];
            }
        }
        const oldChangeGates = AppContext.getDefaultOldChangeGates();
        const oldChangeGatesKeys = Object.keys(oldChangeGates);
        for (const cgKey of oldChangeGatesKeys){
            if (ecsConfig?.[cgKey] != null) {
                oldChangeGates[cgKey] = ecsConfig[cgKey];
            }
        }
        const changeGates = AppContext.getDefaultChangeGates();
        const changeGatesKeys = Object.keys(changeGates);
        for (const cgKey of changeGatesKeys){
            if (ecsConfig?.[cgKey] != null) {
                changeGates[cgKey] = ecsConfig[cgKey];
            }
        }
        const settings = AppContext.getDefaultSettings();
        const settingsKeys = Object.keys(settings);
        for (const settingsKey of settingsKeys){
            if (ecsConfig?.[settingsKey] != null) {
                if (typeof settings[settingsKey] === "object") {
                    settings[settingsKey] = merge(settings[settingsKey], ecsConfig[settingsKey]);
                } else {
                    settings[settingsKey] = ecsConfig[settingsKey];
                }
            }
        }
        // Construct the AugloopFlights string by concatenated JSON config values with a key of AugloopFlights
        // NOTE: The SDK appears to remove duplicate flights and semicolons when it makes the network call to set up the session
        // so extra semicolons and duplicate flight values are OK
        settings.AugloopFlights = Object.values(ecsConfig || {}).filter(isAugloopFlight).map((val)=>val.AugloopFlights).concat(settings.CopilotAugloopFlights) // Remove this line when graduating CopilotAugloopFlights
        .join(";");
        return [
            flights,
            changeGates,
            oldChangeGates,
            settings
        ];
    }
    /**
     * Checks for missing parameters in the session metadata object returned from service and sends back the same object
     * if no missing parameters are found. Throws ConfigNotFoundError if any parameter is missing.
     */ static getSessionMetaData(serverSessionMetaData) {
        const sessionMetaData = {
            appMetadata: {
                clientFlavor: ClientFlavor.App,
                clientType: ClientType.Teams
            },
            appLoadCorrelationId: "sampleAppLoadCorrelationId",
            clientBuild: "sampleClientBuild",
            serverBuild: "sampleServerBuild",
            environment: Environment.Ppe,
            sessionId: "sampleSessionId",
            tenantRegion: TenantRegion.Row
        };
        const sessionMetaDataKeys = Object.keys(sessionMetaData);
        for (const sessionMetaDataKey of sessionMetaDataKeys){
            if (!serverSessionMetaData[sessionMetaDataKey]) {
                throw new ConfigNotFoundError(sessionMetaDataKey);
            }
        }
        return serverSessionMetaData;
    }
}
/** Returns true if the input fulfills the IAugloopFlightConfig interface */ const isAugloopFlight = (config)=>typeof config?.AugloopFlights === "string";
